import { StandardLibraryTable } from '@/components/table/StandardLibraryTable';
import { TablePlaceholder } from '@/components/TablePlaceholder';
import { TextField } from '@/components/TextField';
import { useLocale } from '@/providers/LocaleProvider';
import AiSparkleIcon from '@/resources/images/icons-ai-sparkle.svg';
import BriefcaseIcon from '@/resources/images/icons-briefcase.svg';
import { KLAYO_COLORS } from '@/themes';
import { track } from '@/common/track';
import { Box, Stack, Typography } from '@mui/material';
import { JobBuilderEvent } from 'klayowebshared';
import debounce from 'lodash/debounce';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useGetBestLibraryJobMatchByText } from './hooks/useGetBestLibraryJobMatchByText';
import { JobBuilderSectionRef } from './jobBuilder.types';

interface Props {
  onSelectionChange?: (hasSelection: boolean, jobName?: string) => void;
}

export const JobBuilderKISection = forwardRef<JobBuilderSectionRef, Props>(
  ({ onSelectionChange }, ref) => {
    const { translate } = useLocale();

    const [queryText, setQueryText] = useState('');
    const [inputValue, setInputValue] = useState('');
    const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

    const hasQuery = queryText.length > 0;

    const { match, isFetching } = useGetBestLibraryJobMatchByText({ text: queryText });
    const rows = match?.candidates ?? [];

    // Auto-select the AI-picked row once the response arrives and the user
    // hasn't already chosen one. Tracked per-query so we don't re-overwrite
    // a manual selection after a subsequent fetch settles for the same text.
    const lastAutoPickedQueryRef = useRef<string | null>(null);
    useEffect(() => {
      if (!hasQuery) return;
      if (selectedRowId) return;
      if (lastAutoPickedQueryRef.current === queryText) return;
      const matchedId =
        match?.isSuccess && match.standardLibraryJobDefinitionId
          ? match.standardLibraryJobDefinitionId
          : null;
      if (!matchedId) return;
      const matchedRow = rows.find((r) => r.id === matchedId);
      if (!matchedRow) return;
      lastAutoPickedQueryRef.current = queryText;
      setSelectedRowId(matchedId);
      onSelectionChange?.(true, (matchedRow as any).name ?? (matchedRow as any).jobName);
    }, [hasQuery, queryText, match, rows, selectedRowId, onSelectionChange]);

    useImperativeHandle(ref, () => ({
      submit: () => Promise.resolve(selectedRowId ?? ''),
    }));

    const debouncedSetQuery = useCallback(
      debounce((value: string) => {
        if (value) track(JobBuilderEvent.SelectIntelligenceSearch, { text: value });
        setQueryText(value);
        setSelectedRowId(null);
        lastAutoPickedQueryRef.current = null;
      }, 300),
      []
    );

    const handleSearchChange = (e: any) => {
      setInputValue(e.target.value);
      debouncedSetQuery(e.target.value);
    };

    return (
      <Stack gap={1.5} height='100%' flex={1}>
        <TextField
          value={inputValue}
          onChange={handleSearchChange}
          placeholder={translate('jobBuilder.searchPlaceholder')}
          fullWidth
          clearable={false}
          autoComplete={false}
          leadingIcon={<AiSparkleIcon width={20} height={20} color={KLAYO_COLORS.NeonBlueNB500} />}
        />

        <Typography
          variant='caption'
          color={KLAYO_COLORS.GreyGR400}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}
        >
          <AiSparkleIcon width={12} height={12} color={KLAYO_COLORS.GreyGR400} />
          {translate('jobBuilder.poweredByKI')}
        </Typography>

        {!hasQuery ? (
          <Stack flex={1} alignItems='center' justifyContent='center' gap={2}>
            <AiSparkleIcon width={48} height={48} color={KLAYO_COLORS.NeonBlueNB100} />
            <Stack alignItems='center' gap={1}>
              <Typography
                variant='body1'
                fontSize={18}
                fontWeight={600}
                color={KLAYO_COLORS.GreyGR900}
              >
                {translate('jobBuilder.startWithKI')}
              </Typography>
              <Typography
                variant='body2'
                color={KLAYO_COLORS.GreyGR500}
                textAlign='center'
                sx={{ maxWidth: 500 }}
              >
                {translate('jobBuilder.kiEmptyDescription')}
              </Typography>
            </Stack>
          </Stack>
        ) : (
          <Box
            sx={{
              '& .MuiDataGrid-row': {
                border: `2px solid transparent !important`,
                boxSizing: 'border-box',

                '&[aria-selected="true"]': {
                  borderColor: `${KLAYO_COLORS.NeonBlueNB500} !important`,
                  backgroundColor: `${KLAYO_COLORS.WhiteWH900} !important`,
                },
              },
            }}
          >
            <StandardLibraryTable
              totalCount={rows.length}
              rowHasAction={true}
              translate={translate}
              dense={true}
              paper={false}
              theme={null}
              queryText={queryText}
              showHeader={false}
              rowHeight={82}
              isLoading={isFetching}
              columns={[{ type: StandardLibraryTable.columns.jobSummary }]}
              paginationMode='client'
              rows={rows as any}
              rowSelectionModel={selectedRowId ? [selectedRowId] : []}
              onJobSelect={(_: any, row: any) => {
                const next = selectedRowId === row.id ? null : row.id;
                if (next !== null) {
                  track(JobBuilderEvent.SelectIntelligenceSelectJob, {
                    selection: row.name ?? row.jobName,
                  });
                }
                setSelectedRowId(next);
                onSelectionChange?.(next !== null, row.name ?? row.jobName);
              }}
              showFooter={false}
              hideFirstLastBorder
              noResultsComponent={
                <TablePlaceholder
                  text={translate('standardsLibrary.noMatchingJobs')}
                  icon={<BriefcaseIcon />}
                />
              }
              noRowsComponent={
                <TablePlaceholder
                  text={translate('standardsLibrary.noMatchingJobs')}
                  icon={<BriefcaseIcon />}
                />
              }
            />
          </Box>
        )}
      </Stack>
    );
  }
);

JobBuilderKISection.displayName = 'JobBuilderKISection';
